import React from 'react'
import { Box, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import {
  Carousel as CarouselType,
  CarouselVariants,
  Slide as SlideType,
} from '../../types/carousel'
import Carousel from './Carousel'
import PackageSection from '../Packages/PackageSection'
import HeroTextSection from '../HeroTextSection'
import Image from '../ContentfulEntries/Image'
import HeroTextTwoColumnSection from '../HeroTextTwoColumnSection'
import ProductPackageSection from '../ProductPackages/ProductPackageSection'
import useHide from '../../hooks/useHide'
import { TestimonialSlide } from '../../components/Critique/SideTestimonials'

export interface CarouselSectionProps {
  carouselSection: CarouselType
  transparentBackground?: boolean
}

interface SlideProps {
  key: string
  slide: SlideType
}

const CarouselSection: React.FC<CarouselSectionProps> = ({
  carouselSection,
  transparentBackground,
}) => {
  const { classes, cx } = useStyles()
  const hideClasses = useHide(carouselSection.hideOn)
  return (
    <Box
      className={cx({
        [classes.sectionWithGrayBackground]: !transparentBackground,
      })}
    >
      <Box
        className={cx(hideClasses, {
          [classes.imageContainer]:
            carouselSection.variant === CarouselVariants.Multiple,
        })}
      >
        <Box className={classes.titleContainer}>
          <Typography variant="h2" className={classes.title}>
            {carouselSection?.title}
          </Typography>
        </Box>
        <Carousel
          arrows={
            carouselSection.variant !== CarouselVariants.Multiple &&
            carouselSection.arrows !== 'None'
          }
          dots={carouselSection.breadcrumbs !== 'None'}
          dotsClass={
            carouselSection.breadcrumbs === 'Left'
              ? carouselSection.variant === CarouselVariants.Half
                ? classes.breadcrumbsHalfLeft
                : classes.breadcrumbsLeft
              : classes.breadcrumbs
          }
          slidesToShow={carouselSection.noOfSlides || 1}
          responsiveSettings={
            carouselSection.variant === CarouselVariants.Multiple
              ? responsiveSettings
              : null
          }
        >
          {carouselSection.slides &&
            carouselSection.slides.map((slide: SlideType) => (
              <CarouselSlides key={slide.contentful_id} slide={slide} />
            ))}
        </Carousel>
      </Box>
    </Box>
  )
}

const CarouselSlides: React.FC<SlideProps> = ({ slide }) => {
  const { classes } = useStyles()
  switch (slide.__typename) {
    case 'ContentfulPackageSection':
      return (
        <PackageSection
          inCarousel
          key={slide.contentful_id}
          packageSection={slide}
        />
      )
    case 'ContentfulHeroTextSection':
      return <HeroTextSection row={slide} />
    case 'ContentfulHeroTextTwoColumnSection':
      return (
        <Box className={classes.container}>
          <HeroTextTwoColumnSection row={slide} />
        </Box>
      )
    case 'ContentfulImageInsert':
      return (
        <Box className={classes.image}>
          <Image asset={slide.images[0]} />
        </Box>
      )
    case 'ContentfulProductPackageSection':
      return (
        <ProductPackageSection
          inCarousel
          key={slide.contentful_id}
          packageSection={slide}
        />
      )
    case 'SimpleSlide':
      return <TestimonialSlide body={slide.body} name={slide.name} />
    default:
      return null
  }
}

const responsiveSettings = [
  {
    breakpoint: 1432,
    settings: {
      slidesToShow: 8,
    },
  },
  {
    breakpoint: 1080,
    settings: {
      slidesToShow: 6,
    },
  },
  {
    breakpoint: 760,
    settings: {
      slidesToShow: 4,
    },
  },
]

const useStyles = makeStyles()((theme) => ({
  sectionWithGrayBackground: {
    backgroundColor: theme.palette.background.paper,
  },
  container: {
    paddingLeft: 'calc((100% - 91rem)/2 + 5.5rem)',
    paddingRight: 'calc((100% - 91rem)/2 + 5.5rem)',
    [theme.breakpoints.down('md')]: {
      paddingLeft: '6%',
      paddingRight: '6%',
    },
  },
  breadcrumbs: {
    '&&': {
      [theme.breakpoints.down('md')]: {
        bottom: '2.1875rem',
      },
    },
  },
  breadcrumbsLeft: {
    textAlign: 'left',

    '&&': {
      bottom: 'initial',
      margin: '-7rem 0 7rem calc(6% + 6.93em)',
      width: 'initial',

      [theme.breakpoints.down('lg')]: {
        margin: '-7rem 0 7rem calc(6% + 2em)',
      },
      [theme.breakpoints.down('md')]: {
        margin: '-5rem 0',
        textAlign: 'center',
        width: '100%',
      },
      '@media (min-width: 1456px)': {
        margin: '-7rem 0 7rem calc((100% - 91rem)/2 + 5.5rem + 1.93em)',
      },
    },
  },
  breadcrumbsHalfLeft: {
    textAlign: 'left',
    '&&': {
      width: 'initial',
      [theme.breakpoints.down('md')]: {
        margin: '2rem 0',
        textAlign: 'center',
        width: '100%',
      },
    },
  },
  image: {
    paddingRight: '.5rem',
    maxWidth: '11.5rem',
  },
  imageContainer: {
    padding: '.5625rem 0',
  },
  titleContainer: {
    paddingTop: '1rem',
    paddingBottom: '0',
  },
  title: {
    margin: '0',
    textAlign: 'center',
  },
}))

export default CarouselSection
